/**
 * Minimalistisch voet-SVG (pilot 1; pre-launch design-pass). 4 panels:
 *   linker voet (boven + onder) + rechter voet (boven + onder).
 * Klikbare zones gemapt op backend PijnLocatie-enum.
 * Tap-target ≥ 48x48px (R-zones ≥ 32px → click-area >= 64x64 effective).
 *
 * Op kleine devices toont een 'Lijst-modus'-toggle een alternatieve invoer.
 */
import React, { useState } from "react";
import { PIJN_LOCATIES, PIJN_LOCATIE_LABELS, PIJN_ZIJDES, PIJN_ZIJDE_LABELS } from "../../lib/constants";

// Zone-coördinaten per voet+perspectief — minimaal anatomisch
const ZONES = [
  { locatie: "tenen",      cx: 60,  cy: 35  },
  { locatie: "voorvoet",   cx: 60,  cy: 75  },
  { locatie: "midvoet",    cx: 60,  cy: 120 },
  { locatie: "achtervoet", cx: 60,  cy: 165 },
  { locatie: "enkel",      cx: 60,  cy: 205 },
];

function FootSvg({ side, view, onZoneClick, testid }) {
  // side = "links" | "rechts" ; view = "boven" | "onder"
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="mb-1 text-xs font-medium text-slate-600">
        {side === "links" ? "Linker" : "Rechter"} voet — {view === "boven" ? "boven" : "onder"}
      </div>
      <svg width="120" height="240" viewBox="0 0 120 240" role="img"
        aria-label={`${side} voet ${view}`} className="rounded-md border border-slate-300 bg-slate-50">
        {/* Voet-omtrek */}
        <ellipse cx="60" cy="125" rx="38" ry="100" fill="#ffffff" stroke="#94a3b8" strokeWidth="2" />
        {/* Zones */}
        {ZONES.map((z) => (
          <g key={z.locatie} role="button" tabIndex={0}
             onClick={() => onZoneClick({ locatie: z.locatie, voet: side, view })}
             onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onZoneClick({ locatie: z.locatie, voet: side, view }); }}
             aria-label={`${PIJN_LOCATIE_LABELS[z.locatie]} ${side}`}
             data-testid={`zone-${side}-${view}-${z.locatie}`}
             className="cursor-pointer outline-none focus:opacity-75">
            <circle cx={z.cx} cy={z.cy} r="20" fill="#bae6fd" stroke="#0284c7" strokeWidth="2" opacity="0.85" />
            <text x={z.cx} y={z.cy + 4} textAnchor="middle" fontSize="9" fill="#0c4a6e" pointerEvents="none">
              {PIJN_LOCATIE_LABELS[z.locatie]}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

/**
 * Hybride pijnplek-invoer.
 * Props:
 *   plekken: [{tempId, locatie, zijde, voet, pijncijfer, notitie}]
 *   onAdd(plek), onRemove(tempId)
 */
export function VoetDiagram({ plekken, onAdd, onRemove }) {
  const [listMode, setListMode] = useState(false);
  const [pending, setPending] = useState(null); // { locatie, voet, view }

  function handleZoneClick({ locatie, voet, view }) {
    setPending({ locatie, voet, view });
  }

  function commit({ zijde, pijncijfer, notitie }) {
    if (!pending) return;
    onAdd({
      tempId: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      locatie: pending.locatie,
      voet: pending.voet,
      zijde,
      pijncijfer,
      notitie: notitie || null,
    });
    setPending(null);
  }

  return (
    <div data-testid="voet-diagram">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-slate-700">Klik op een zone om een pijnplek toe te voegen.</p>
        <button type="button" onClick={() => setListMode(!listMode)} data-testid="toggle-list-mode"
          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50">
          {listMode ? "SVG-modus" : "Lijst-modus"}
        </button>
      </div>

      {!listMode ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <FootSvg side="links" view="boven" onZoneClick={handleZoneClick} testid="foot-links-boven" />
          <FootSvg side="links" view="onder" onZoneClick={handleZoneClick} testid="foot-links-onder" />
          <FootSvg side="rechts" view="boven" onZoneClick={handleZoneClick} testid="foot-rechts-boven" />
          <FootSvg side="rechts" view="onder" onZoneClick={handleZoneClick} testid="foot-rechts-onder" />
        </div>
      ) : (
        <ListModeForm onCommit={(d) => { setPending({ locatie: d.locatie, voet: d.voet, view: "n.v.t." }); }} />
      )}

      {pending ? (
        <PijnplekModal pending={pending} onClose={() => setPending(null)} onCommit={commit} />
      ) : null}

      <h3 className="mt-6 text-sm font-semibold text-slate-700">Toegevoegde plekken ({plekken.length})</h3>
      <ul className="mt-2 divide-y divide-slate-200 rounded-md border border-slate-200 bg-white" data-testid="plekken-lijst">
        {plekken.length === 0 ? (
          <li className="px-4 py-3 text-sm text-slate-500" data-testid="plekken-leeg">Nog geen plekken toegevoegd.</li>
        ) : plekken.map((p) => (
          <li key={p.tempId} className="flex items-center justify-between gap-3 px-4 py-2 text-sm" data-testid={`plek-${p.tempId}`}>
            <div>
              <span className="font-medium">{PIJN_LOCATIE_LABELS[p.locatie]}</span>{" "}
              <span className="text-slate-500">— {p.voet} ({PIJN_ZIJDE_LABELS[p.zijde]})</span>
              {p.pijncijfer >= 7 ? (
                <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-xs font-medium text-rose-800" data-testid={`plek-flag-${p.tempId}`}>
                  ⚑ FLAG ({p.pijncijfer})
                </span>
              ) : (
                <span className="ml-2 text-xs text-slate-600" data-testid={`plek-cijfer-${p.tempId}`}>pijn {p.pijncijfer}</span>
              )}
              {p.notitie ? <span className="ml-2 text-xs text-slate-500">"{p.notitie}"</span> : null}
            </div>
            <button type="button" onClick={() => onRemove(p.tempId)} data-testid={`btn-remove-plek-${p.tempId}`}
              className="rounded-md border border-rose-300 px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50">
              Verwijderen
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ListModeForm({ onCommit }) {
  const [locatie, setLocatie] = useState("voorvoet");
  const [voet, setVoet] = useState("links");
  return (
    <div className="grid grid-cols-1 gap-3 rounded-md border border-slate-200 bg-white p-4 sm:grid-cols-3" data-testid="list-mode-form">
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Locatie</span>
        <select value={locatie} onChange={(e) => setLocatie(e.target.value)} aria-label="Locatie kiezen" data-testid="list-mode-locatie"
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2">
          {PIJN_LOCATIES.map((l) => <option key={l} value={l}>{PIJN_LOCATIE_LABELS[l]}</option>)}
        </select>
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Voet</span>
        <select value={voet} onChange={(e) => setVoet(e.target.value)} aria-label="Voet kiezen" data-testid="list-mode-voet"
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2">
          <option value="links">Links</option>
          <option value="rechts">Rechts</option>
        </select>
      </label>
      <button type="button" onClick={() => onCommit({ locatie, voet })} data-testid="list-mode-next"
        className="self-end rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
        Pijn-info invoeren
      </button>
    </div>
  );
}

function PijnplekModal({ pending, onClose, onCommit }) {
  const [zijde, setZijde] = useState("onder");
  const [cijfer, setCijfer] = useState(5);
  const [notitie, setNotitie] = useState("");
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4" data-testid="modal-pijnplek">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">{PIJN_LOCATIE_LABELS[pending.locatie]} — {pending.voet}</h3>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-slate-700">Zijde</span>
          <select value={zijde} onChange={(e) => setZijde(e.target.value)} aria-label="Zijde kiezen" data-testid="modal-pijnplek-zijde"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2">
            {PIJN_ZIJDES.map((z) => <option key={z} value={z}>{PIJN_ZIJDE_LABELS[z]}</option>)}
          </select>
        </label>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-slate-700">Pijncijfer: {cijfer}</span>
          <input type="range" min="0" max="10" value={cijfer} onChange={(e) => setCijfer(parseInt(e.target.value, 10))}
            data-testid="modal-pijnplek-cijfer" aria-label="Pijncijfer"
            className="mt-2 h-12 w-full" />
          <div className="flex justify-between text-xs text-slate-500">
            <span>0 geen pijn</span><span>10 ergste pijn</span>
          </div>
          {cijfer >= 7 ? (
            <div className="mt-2 rounded bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700" data-testid="modal-pijnplek-flag-waarschuwing">
              ⚑ Pijncijfer ≥ 7 — automatische flag voor podotherapeut wordt aangemaakt
            </div>
          ) : null}
        </label>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-slate-700">Notitie (optioneel)</span>
          <textarea value={notitie} onChange={(e) => setNotitie(e.target.value)} maxLength={500} rows={2}
            data-testid="modal-pijnplek-notitie" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="modal-pijnplek-cancel"
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">Annuleren</button>
          <button type="button" onClick={() => onCommit({ zijde, pijncijfer: cijfer, notitie })} data-testid="modal-pijnplek-submit"
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white">Toevoegen</button>
        </div>
      </div>
    </div>
  );
}
